import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorScheduleData,
  selectTargetAccumulatorScheduleFieldValidation,
  selectTargetAccumulatorScheduleTable,
} from '@/store/targetAccumulator/selectors.ts';
import type { AccumulatorScheduleLine } from '@/store/targetAccumulator/state.ts';

const Leverage = ({ legId }: { legId: string }) => {
  const tileId = useTargetAccumulatorTileId();
  const leverage = useAppSelector(
    (state) => selectTargetAccumulatorScheduleTable(state, tileId, legId)?.leverageAmount,
  );
  const isModified = useAppSelector(
    (state) =>
      selectTargetAccumulatorScheduleData(state, tileId).scheduleCustomFields?.[legId]?.includes('LeverageAmount') ??
      false,
  );
  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { validation } = useAppSelector((state) =>
    selectTargetAccumulatorScheduleFieldValidation(state, tileId, legId, 'leverageAmountString'),
  );

  const handleLeverage = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Schedule leverage changed',
      isDefined(value) ? String(value) : 'null',
    );
    return checkTargetAccumulatorWithTradeCapture({
      schedule: { [legId]: { leverageAmount: isDefined(value) ? value : null } as AccumulatorScheduleLine },
    });
  };

  return (
    <TradeCaptureValidationWrapper validation={validation}>
      <SgNumberInput
        valuePosition="start"
        selectAllOnFocus
        acceleratorsEnabled
        className={isModified ? 'text-info' : ''}
        value={isDefined(leverage) ? leverage : undefined}
        maxDecimals={9}
        onChange={handleLeverage}
        size="sm"
        data-e2e="leverage"
        data-e2e-is-modified={isModified}
      />
    </TradeCaptureValidationWrapper>
  );
};

export default Leverage;
